import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import {
  ArrowLeft, Globe, MapPin, Building2, Users, GraduationCap, BookOpen,
  Shield, ExternalLink, ChevronDown, Pencil,
} from 'lucide-react';
import { useImpersonate } from '../../hooks/useImpersonate';
import { GestorCard } from '../../components/gestao/GestorCard';

const API = import.meta.env.VITE_API_URL || '/api/v1';
const getToken = () => localStorage.getItem('ac_token');
const headers = () => ({ Authorization: `Bearer ${getToken()}` });

/* ───────── Hooks ───────── */

function useEstadoDetalhe(id: string) {
  return useQuery({
    queryKey: ['estado-detalhe', id],
    queryFn: async () => {
      const res = await fetch(`${API}/admin/estados/${id}`, { headers: headers() });
      if (!res.ok) throw new Error('Falha');
      return res.json();
    },
    enabled: !!id,
  });
}

function useEstadoStats(id: string) {
  return useQuery({
    queryKey: ['estado-stats', id],
    queryFn: async () => {
      const res = await fetch(`${API}/admin/estados/${id}/stats`, { headers: headers() });
      if (!res.ok) throw new Error('Falha');
      return res.json();
    },
    enabled: !!id,
  });
}

function useEstadoGestores(estadoId: string) {
  return useQuery({
    queryKey: ['estado-gestores', estadoId],
    queryFn: async () => {
      const res = await fetch(`${API}/admin/usuarios`, { headers: headers() });
      if (!res.ok) throw new Error('Falha');
      const all = await res.json();
      const list = Array.isArray(all) ? all : all.data || [];
      return list.filter((u: any) => u.estadoId === estadoId && u.perfil === 'ESTADUAL');
    },
    enabled: !!estadoId,
  });
}

function useEstadoMunicipios(estadoId: string) {
  return useQuery({
    queryKey: ['estado-municipios-detalhe', estadoId],
    queryFn: async () => {
      const res = await fetch(`${API}/admin/municipios?estadoId=${estadoId}`, { headers: headers() });
      if (!res.ok) return [];
      const all = await res.json();
      return Array.isArray(all) ? all : all.data || [];
    },
    enabled: !!estadoId,
  });
}

function useEstadoEscolas(estadoId: string) {
  return useQuery({
    queryKey: ['estado-escolas-detalhe', estadoId],
    queryFn: async () => {
      const res = await fetch(`${API}/admin/escolas`, { headers: headers() });
      if (!res.ok) return [];
      const all = await res.json();
      const list = Array.isArray(all) ? all : all.data || [];
      return list.filter((e: any) => e.municipio?.estado?.id === estadoId || e.municipio?.estadoId === estadoId);
    },
    enabled: !!estadoId,
  });
}

/* ───────── STAT CARD ───────── */

function StatCard({ icon, value, label, color }: { icon: React.ReactNode; value: number | string; label: string; color: string }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
      background: 'rgba(255,255,255,.12)', borderRadius: 12, flex: 1, minWidth: 110,
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: `${color}30`, color: 'white',
      }}>{icon}</div>
      <div>
        <div style={{ fontWeight: 800, fontSize: '1.3rem', color: 'white', lineHeight: 1 }}>{value}</div>
        <div style={{ fontSize: '.68rem', color: 'rgba(255,255,255,.7)', fontWeight: 500, marginTop: 2 }}>{label}</div>
      </div>
    </div>
  );
}

/* GestorCard importado de ../../components/gestao/GestorCard */

/* ───────── MAIN PAGE ───────── */

export function EstadoDetalhePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: estado, isLoading } = useEstadoDetalhe(id || '');
  const { data: stats } = useEstadoStats(id || '');
  const { data: gestores } = useEstadoGestores(id || '');
  const { data: municipios } = useEstadoMunicipios(id || '');
  const { data: escolas } = useEstadoEscolas(id || '');
  const { impersonate: handleImpersonate, impersonating } = useImpersonate();
  const [collapsedMun, setCollapsedMun] = useState<Set<string>>(new Set());

  if (isLoading) return <div className="admin-page animate-fadeIn" style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Carregando...</div>;
  if (!estado) return <div className="admin-page animate-fadeIn" style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Estado não encontrado</div>;

  const tipoRedeColors: Record<string, string> = { MUNICIPAL: '#10b981', ESTADUAL: '#6366f1', PARTICULAR: '#f59e0b', FEDERAL: '#0ea5e9' };
  const gestorList: any[] = gestores || [];
  const munList: any[] = municipios || [];
  const escolaList: any[] = escolas || [];

  // Group escolas by municipio
  const escolasByMun = new Map<string, { munNome: string; munId: string; escolas: any[] }>();
  escolaList.forEach((e: any) => {
    const munId = e.municipioId || e.municipio?.id || '__sem__';
    const munNome = e.municipio?.nome || e.cidade || 'Sem Município';
    if (!escolasByMun.has(munId)) escolasByMun.set(munId, { munNome, munId, escolas: [] });
    escolasByMun.get(munId)!.escolas.push(e);
  });
  const escolaGroups = [...escolasByMun.values()].sort((a, b) => a.munNome.localeCompare(b.munNome));

  const toggleMun = (munId: string) => {
    const next = new Set(collapsedMun);
    next.has(munId) ? next.delete(munId) : next.add(munId);
    setCollapsedMun(next);
  };

  return (
    <div className="admin-page animate-fadeIn" style={{ maxWidth: 1100, margin: '0 auto' }}>
      {/* ═══ HERO ═══ */}
      <div style={{
        background: 'linear-gradient(135deg, #065f46, #059669, #10b981)',
        borderRadius: 20, padding: '28px 32px 24px', marginBottom: 24,
        color: 'white', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', inset: 0, opacity: .06, backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '20px 20px' }} />

        <div style={{ display: 'flex', gap: 8, marginBottom: 16, position: 'relative', zIndex: 1 }}>
          <button onClick={() => navigate('/master/estados')} style={{
            display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,.15)',
            border: 'none', color: 'white', padding: '6px 14px', borderRadius: 8,
            cursor: 'pointer', fontSize: '.78rem', fontWeight: 600,
          }}>
            <ArrowLeft size={14} /> Voltar para Estados
          </button>
          <button onClick={() => navigate(`/master/estados`, { state: { editId: id } })} style={{
            display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,.15)',
            border: 'none', color: 'white', padding: '6px 14px', borderRadius: 8,
            cursor: 'pointer', fontSize: '.78rem', fontWeight: 600,
          }}>
            <Pencil size={14} /> Editar Estado
          </button>
        </div>

        <div style={{ fontSize: '.68rem', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: 'rgba(255,255,255,.6)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Globe size={14} /> ESTADO
        </div>
        <h1 style={{ fontSize: '1.8rem', fontWeight: 900, margin: '0 0 6px', lineHeight: 1.1 }}>
          {estado.nome} <span style={{ fontWeight: 600, fontSize: '1.2rem', opacity: .7 }}>({estado.sigla})</span>
        </h1>
        <div style={{ fontSize: '.85rem', color: 'rgba(255,255,255,.75)', fontWeight: 500 }}>
          {estado.rede?.nome || 'Sem Rede'}
        </div>

        <div style={{ display: 'flex', gap: 12, marginTop: 20, flexWrap: 'wrap' }}>
          <StatCard icon={<MapPin size={18} />} value={stats?.municipios || munList.length} label="Municípios" color="#0ea5e9" />
          <StatCard icon={<Building2 size={18} />} value={stats?.escolas?.total || 0} label="Escolas" color="#3b82f6" />
          <StatCard icon={<Users size={18} />} value={stats?.alunos?.total || 0} label="Alunos" color="#f59e0b" />
          <StatCard icon={<GraduationCap size={18} />} value={stats?.professores?.total || 0} label="Professores" color="#8b5cf6" />
          <StatCard icon={<BookOpen size={18} />} value={stats?.turmas?.total || 0} label="Turmas" color="#10b981" />
        </div>
      </div>

      {/* ═══ CONTENT ═══ */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' }}>

        {/* ── GESTORES ── */}
        <div style={{
          gridColumn: gestorList.length > 2 ? '1 / -1' : 'auto',
          padding: '20px 22px', borderRadius: 16,
          background: 'var(--bg-card, white)', border: '1px solid var(--border-color, #e5e7eb)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <Shield size={18} style={{ color: '#10b981' }} />
            <span style={{ fontWeight: 700, fontSize: '.95rem', color: 'var(--text-main)' }}>Gestores Estaduais</span>
            <span style={{ padding: '2px 8px', borderRadius: 8, fontSize: '.68rem', fontWeight: 700, background: '#10b98110', color: '#10b981' }}>{gestorList.length}</span>
          </div>
          {gestorList.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-muted)', fontSize: '.82rem', fontStyle: 'italic' }}>
              Nenhum gestor estadual cadastrado
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {gestorList.map((g: any) => <GestorCard key={g.id} user={g} onImpersonate={handleImpersonate} impersonating={impersonating} />)}
            </div>
          )}
        </div>

        {/* ── MUNICÍPIOS ── */}
        <div style={{
          gridColumn: munList.length > 3 ? '1 / -1' : 'auto',
          padding: '20px 22px', borderRadius: 16,
          background: 'var(--bg-card, white)', border: '1px solid var(--border-color, #e5e7eb)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <MapPin size={18} style={{ color: '#f59e0b' }} />
            <span style={{ fontWeight: 700, fontSize: '.95rem', color: 'var(--text-main)' }}>Municípios</span>
            <span style={{ padding: '2px 8px', borderRadius: 8, fontSize: '.68rem', fontWeight: 700, background: '#f59e0b10', color: '#f59e0b' }}>{munList.length}</span>
          </div>
          {munList.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-muted)', fontSize: '.82rem', fontStyle: 'italic' }}>
              Nenhum município vinculado
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {munList.map((m: any) => (
                <div key={m.id} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
                  borderRadius: 10, border: '1px solid var(--border-color, #e5e7eb)',
                  background: 'var(--bg-main, #f9fafb)', transition: 'all .15s',
                }}
                  onMouseEnter={ev => { (ev.currentTarget as HTMLElement).style.borderColor = '#f59e0b'; (ev.currentTarget as HTMLElement).style.boxShadow = '0 2px 8px rgba(245,158,11,.1)'; }}
                  onMouseLeave={ev => { (ev.currentTarget as HTMLElement).style.borderColor = ''; (ev.currentTarget as HTMLElement).style.boxShadow = ''; }}
                >
                  <MapPin size={16} style={{ color: '#f59e0b', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: '.85rem', color: 'var(--text-main)' }}>{m.nome}</div>
                    {m.codigoIBGE && <div style={{ fontSize: '.68rem', color: 'var(--text-muted)' }}>IBGE: {m.codigoIBGE}</div>}
                  </div>
                  <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: '.62rem', fontWeight: 700, background: '#3b82f612', color: '#3b82f6' }}>
                    {m._count?.escolas || 0} escolas
                  </span>
                  <Link to={`/master/municipios/${m.id}`} style={{
                    width: 28, height: 28, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: '#6366f108', color: '#6366f1', border: '1px solid #6366f120', textDecoration: 'none',
                  }} title="Ver detalhe">
                    <ExternalLink size={13} />
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── ESCOLAS AGRUPADAS POR MUNICÍPIO ── */}
        <div style={{
          gridColumn: '1 / -1', padding: '20px 22px', borderRadius: 16,
          background: 'var(--bg-card, white)', border: '1px solid var(--border-color, #e5e7eb)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <Building2 size={18} style={{ color: '#6366f1' }} />
            <span style={{ fontWeight: 700, fontSize: '.95rem', color: 'var(--text-main)' }}>Escolas do Estado</span>
            <span style={{ padding: '2px 8px', borderRadius: 8, fontSize: '.68rem', fontWeight: 700, background: '#6366f110', color: '#6366f1' }}>{escolaList.length}</span>
          </div>

          {escolaGroups.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-muted)', fontSize: '.82rem', fontStyle: 'italic' }}>
              Nenhuma escola vinculada
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {escolaGroups.map(group => {
                const isCollapsed = collapsedMun.has(group.munId);
                return (
                  <div key={group.munId} style={{ borderRadius: 10, border: '1px solid var(--border-color, #e5e7eb)', overflow: 'hidden' }}>
                    <button onClick={() => toggleMun(group.munId)} style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                      padding: '10px 14px', background: 'var(--bg-main, #f9fafb)', border: 'none',
                      cursor: 'pointer', textAlign: 'left',
                    }}>
                      <MapPin size={14} style={{ color: '#f59e0b' }} />
                      <span style={{ flex: 1, fontWeight: 700, fontSize: '.82rem', color: 'var(--text-main)' }}>{group.munNome}</span>
                      <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: '.65rem', fontWeight: 700, background: '#6366f110', color: '#6366f1' }}>{group.escolas.length}</span>
                      <ChevronDown size={14} style={{ color: '#9ca3af', transition: 'transform .2s', transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }} />
                    </button>
                    {!isCollapsed && (
                      <div style={{ padding: '6px 14px 10px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {group.escolas.map((e: any) => {
                          const color = tipoRedeColors[e.tipoRede] || '#6b7280';
                          return (
                            <div key={e.id} style={{
                              display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
                              borderRadius: 8, background: 'var(--bg-card, white)', border: '1px solid var(--border-color, #eee)',
                            }}>
                              <Building2 size={14} style={{ color: '#3b82f6', flexShrink: 0 }} />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 600, fontSize: '.8rem', color: 'var(--text-main)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.nome}</div>
                              </div>
                              <span style={{ padding: '2px 6px', borderRadius: 5, fontSize: '.58rem', fontWeight: 700, background: `${color}15`, color }}>{e.tipoRede}</span>
                              <span style={{ padding: '2px 6px', borderRadius: 5, fontSize: '.58rem', fontWeight: 700, background: '#f59e0b12', color: '#f59e0b' }}>{e._count?.alunos || 0} al.</span>
                              <Link to={`/master/escolas/${e.id}`} style={{
                                width: 24, height: 24, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                background: '#6366f108', color: '#6366f1', border: '1px solid #6366f120', textDecoration: 'none',
                              }} title="Ver detalhe">
                                <ExternalLink size={11} />
                              </Link>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── DETALHES ── */}
        <div style={{
          gridColumn: '1 / -1', padding: '20px 22px', borderRadius: 16,
          background: 'var(--bg-card, white)', border: '1px solid var(--border-color, #e5e7eb)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <Globe size={18} style={{ color: '#059669' }} />
            <span style={{ fontWeight: 700, fontSize: '.95rem', color: 'var(--text-main)' }}>Detalhes do Estado</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
            {[
              { label: 'Nome', value: estado.nome },
              { label: 'Sigla', value: estado.sigla },
              { label: 'Rede', value: estado.rede?.nome || '—' },
              { label: 'Municípios', value: stats?.municipios ?? munList.length },
              { label: 'Escolas Ativas', value: stats?.escolas?.ativas ?? '—' },
              { label: 'Alunos Ativos', value: stats?.alunos?.ativos ?? '—' },
              { label: 'Professores Ativos', value: stats?.professores?.ativos ?? '—' },
              { label: 'Turmas Ativas', value: stats?.turmas?.ativas ?? '—' },
              { label: 'Criado em', value: estado.criadoEm ? new Date(estado.criadoEm).toLocaleDateString('pt-BR') : '—' },
            ].map((item, i) => (
              <div key={i} style={{
                padding: '10px 14px', borderRadius: 10,
                background: 'var(--bg-main, #f8fafc)', border: '1px solid var(--border-color, #e5e7eb)',
              }}>
                <div style={{ fontSize: '.62rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '.5px' }}>{item.label}</div>
                <div style={{ fontSize: '.85rem', fontWeight: 700, color: 'var(--text-main)' }}>{item.value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
